import { StandardEvents } from '@shopify/events';
import VariantPicker from '@theme/variant-picker';

class CardVariantPickerComponent extends VariantPicker {
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    const requestUrl = super.buildRequestUrl(selectedOption, source, sourceSelectedOptionsValues);
    const url = new URL(requestUrl, window.location.origin);
    url.searchParams.set('section_id', 'section-rendering-product-card');
    return url.toString();
  }
}

if (!customElements.get('card-variant-picker-component')) {
  customElements.define('card-variant-picker-component', CardVariantPickerComponent);
}

if (!document.documentElement.dataset.advancedProductCardInitialized) {
  document.documentElement.dataset.advancedProductCardInitialized = 'true';
  document.addEventListener(StandardEvents.productSelect, handleProductSelect, true);
  observeCardSwatches();
}

/** @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event */
function handleProductSelect(event) {
  const target = event.target instanceof Element ? event.target : null;
  const card = target?.closest('product-card.custom-product-card-advanced');
  if (!card || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      const payload = detail?.html?.querySelector('[data-custom-product-card-rendering]');
      if (!payload) return;

      requestAnimationFrame(() => {
        updatePrices(card, payload);
        updateDiscount(card, payload);
        updateGallery(card, detail?.resource);
        updateStandardGallery(card, detail?.resource);
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[advanced-product-card] Variant refresh failed.', error);
    });
}

/** @param {Element} card @param {Element} payload */
function updatePrices(card, payload) {
  for (const role of ['current', 'reference']) {
    const nextBlock = payload.querySelector(`[data-custom-card-price-role='${role}']`);
    const nextContainer = nextBlock?.querySelector("[ref='priceContainer']");
    if (!nextBlock || !nextContainer) continue;

    for (const currentBlock of card.querySelectorAll(`[data-custom-card-price-role='${role}']`)) {
      currentBlock.dataset.productId = nextBlock.dataset.productId || '';
      if (role === 'reference') {
        currentBlock.dataset.referenceSource = nextBlock.dataset.referenceSource || '';
      }

      const currentContainer = currentBlock.querySelector("[ref='priceContainer']");
      currentContainer?.replaceWith(nextContainer.cloneNode(true));

      if (role === 'current') restoreCurrentPriceLabel(currentBlock);
      if (role === 'reference') restoreReferenceLabel(currentBlock);
    }
  }
}

/** @param {Element} block */
function restoreCurrentPriceLabel(block) {
  const container = block.querySelector("[ref='priceContainer'][data-zero-price]");
  const price = container?.querySelector('.price');
  if (price) price.textContent = block.getAttribute('data-zero-price-label') || 'Coming Soon';
}

/** @param {Element} block */
function restoreReferenceLabel(block) {
  const label = block.dataset.referenceSource === 'msrp' ? block.dataset.msrpLabel : block.dataset.compareAtLabel;
  const hiddenLabel = block.querySelector('.visually-hidden');
  const visibleLabel = block.querySelector('.custom-reference-price__label');
  if (hiddenLabel) hiddenLabel.textContent = label ? `${label}\u00a0` : '';
  if (visibleLabel && block.dataset.showLabel !== 'false') visibleLabel.textContent = label || '';
  if (visibleLabel && block.dataset.showLabel === 'false') visibleLabel.remove();
}

/** @param {Element} card @param {Element} payload */
function updateDiscount(card, payload) {
  const next = payload.querySelector('.custom-card-discount');
  if (!(next instanceof HTMLElement)) return;

  for (const current of card.querySelectorAll('.custom-card-discount')) {
    if (!(current instanceof HTMLElement)) continue;
    const source = next.dataset.referenceSource || '';
    const hasDiscount = next.dataset.hasDiscount === 'true';

    current.classList.remove('custom-card-discount--sale', 'custom-card-discount--msrp', 'custom-card-discount--empty');
    if (source) current.classList.add(`custom-card-discount--${source}`);
    current.classList.toggle('custom-card-discount--empty', !hasDiscount);
    current.dataset.referenceSource = source;
    current.dataset.hasDiscount = String(hasDiscount);

    const badge = current.querySelector('.custom-card-discount__badge');
    if (!badge) continue;
    badge.toggleAttribute('hidden', !hasDiscount);
    if (!hasDiscount) {
      badge.textContent = '';
    } else if (source === 'sale') {
      badge.textContent = next.dataset.saleWarning === 'true' ? 'Sale' : current.dataset.saleLabel || 'Sale';
    } else {
      badge.textContent = [next.dataset.savingsDisplay, current.dataset.msrpSuffix].filter(Boolean).join(' ');
    }
  }
}

/** @param {Element} card @param {{ id?: number | string } | null | undefined} variant */
function updateGallery(card, variant) {
  const gallery = card.querySelector('.custom-card-gallery-variant');
  if (!gallery) return;

  const mediaItems = [...gallery.querySelectorAll('.custom-card-gallery-variant__media[data-variant-id]')];
  if (mediaItems.length) {
    const selected = card.querySelector(
      "swatches-variant-picker-component input[type='radio']:checked[data-option-media-id], card-variant-picker-component input[type='radio']:checked[data-option-media-id]"
    );
    let variantId = '';
    if (variant?.id) variantId = String(variant.id);
    else if (selected instanceof HTMLInputElement) variantId = selected.dataset.optionMediaId || '';
    if (!variantId) return;

    for (const media of mediaItems) {
      const selectedMedia = media.getAttribute('data-variant-id') === variantId;
      media.toggleAttribute('hidden', !selectedMedia);
      media.setAttribute('aria-hidden', selectedMedia ? 'false' : 'true');
    }
    return;
  }

  const selected = card.querySelector(
    "swatches-variant-picker-component input[type='radio']:checked[data-option-media-id]"
  );
  if (!(selected instanceof HTMLInputElement)) return;

  const slides = [...gallery.querySelectorAll('slideshow-slide[variant-image]')];
  if (!slides.length) return;
  let firstVisible;

  for (const slide of slides) {
    const selectedSlide = slide.getAttribute('slide-id') === selected.dataset.optionMediaId;
    slide.toggleAttribute('hidden', !selectedSlide);
    slide.removeAttribute('reveal');
    if (selectedSlide && !firstVisible) firstVisible = slide;
  }
  for (const slide of slides) slide.setAttribute('aria-hidden', slide === firstVisible ? 'false' : 'true');
  firstVisible?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
}

/** @param {Element} card @param {{ featured_media?: { id?: number | string } } | null | undefined} variant */
function updateStandardGallery(card, variant) {
  const mediaId = variant?.featured_media?.id;
  if (!mediaId) return;
  const gallery = card.querySelector('.card-gallery:not(.custom-card-gallery-variant)');
  const slideshow = gallery?.querySelector('slideshow-component');
  const slide = gallery?.querySelector(`slideshow-slide[slide-id='${mediaId}']`);
  if (!slide) return;
  slideshow?.select?.({ id: String(mediaId) }, undefined, { animate: false });
}

function observeCardSwatches() {
  revealCardSwatches(document);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) if (node instanceof Element) revealCardSwatches(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

/** @param {Document | Element} root */
function revealCardSwatches(root) {
  for (const list of root.querySelectorAll?.('.custom-product-card-advanced product-swatches overflow-list') || []) {
    customElements.whenDefined('overflow-list').then(() => keepSwatchesVisible(list));
  }
}

/** @param {Element} list */
function keepSwatchesVisible(list) {
  if (!list.isConnected || !list.shadowRoot) return;
  list.removeAttribute('disabled');
  list.dataset.customProductCardSwatchesVisible = 'true';
  const part = list.shadowRoot.querySelector('[part="list"]');
  if (part instanceof HTMLElement) {
    part.style.flexWrap = 'wrap';
    part.style.height = 'auto';
    part.style.overflow = 'visible';
  }
  const more = list.shadowRoot.querySelector('slot[name="more"]');
  if (more instanceof HTMLElement) more.hidden = true;
  for (const swatch of list.querySelectorAll('.variant-option__swatch')) swatch.removeAttribute('slot');
}
